import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrdersByMobileHandler, clearOrdersHandler } from "../redux/handlers";
import AdminLayout from "../../../components/Shared/AdminLayout"
import Container from "../../../components/Shared/Container"
import OrderCard from "../../../components/Admin/Orders/OrderCard"
import Pagination from "../../../components/Shared/Pagination"

const ORDER_STATUSES = [
    { value: "", label: "ALL STATUSES" },
    { value: "VALIDATION_PENDING", label: "VALIDATION PENDING" },
    { value: "VALIDATION_FAILED", label: "VALIDATION FAILED" },
    { value: "PAYMENT_PENDING", label: "PAYMENT PENDING" },
    { value: "PAYMENT_FAILED", label: "PAYMENT FAILED" },
    { value: "GENERATION_PENDING", label: "GENERATION PENDING" },
    { value: "GENERATION_FAILED", label: "GENERATION FAILED" },
    { value: "SUCCESS", label: "SUCCESS" },
    { value: "CANCELLED", label: "CANCELLED" },
    { value: "REFUND_PENDING", label: "REFUND PENDING" },
    { value: "SOURCE_REFUNDED", label: "SOURCE REFUNDED" },
    { value: "WALLET_REFUNDED", label: "WALLET REFUNDED" },
    { value: "REFUND_FAILED", label: "REFUND FAILED" },
    { value: "REFUND_EXHAUSTED", label: "REFUND EXHAUSTED" }
];

const Orders = () => {
    const dispatch = useDispatch();
    const { orders, ordersLoading, ordersTotal, ordersMobileNumber } = useSelector(state => state.admin);
    const [mobileNumber, setMobileNumber] = useState("");
    const [searchDebounce, setSearchDebounce] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(mobileNumber);
        }, 500);

        return () => clearTimeout(timer);
    }, [mobileNumber]);


    // Fetch orders when search debounce or page changes
    useEffect(() => {
        if (searchDebounce.trim().length >= 3) {  // Reduced minimum length for more flexible search
            const params = { 
                page: currentPage, 
                size: itemsPerPage 
            };
            if (selectedStatus) {
                params.status = selectedStatus;
            }
            dispatch(fetchOrdersByMobileHandler(searchDebounce.trim(), params));
        } else if (searchDebounce.trim() === "") {
            // Fetch all orders when search is empty
            const params = { 
                page: currentPage, 
                size: itemsPerPage 
            };
            if (selectedStatus) {
                params.status = selectedStatus;
            }
            dispatch(fetchOrdersByMobileHandler("", params));
        }
    }, [searchDebounce, currentPage, selectedStatus, itemsPerPage, dispatch]);

    const handleSearch = (value) => {
        setMobileNumber(value);
        setCurrentPage(1);
    };

    const handleStatusChange = (status) => {
        setSelectedStatus(status);
        setIsDropdownOpen(false);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleView = (order) => {
        console.log('View order:', order);
        // TODO: Implement order detail view modal
    };

    const getStatusColor = (status) => {
        switch(status) {
            case "SUCCESS":
                return "text-green-500";
            case "VALIDATION_PENDING":
            case "PAYMENT_PENDING":
            case "GENERATION_PENDING":
            case "REFUND_PENDING":
                return "text-yellow-500";
            case "VALIDATION_FAILED":
            case "PAYMENT_FAILED":
            case "GENERATION_FAILED":
            case "REFUND_FAILED":
                return "text-red-500";
            case "CANCELLED":
            case "REFUND_EXHAUSTED":
                return "text-gray-500";
            case "SOURCE_REFUNDED":
            case "WALLET_REFUNDED":
                return "text-blue-500";
            default:
                return "text-white";
        }
    };

    // Calculate total pages
    const totalPages = Math.ceil(ordersTotal / itemsPerPage) || 1;

    return (
        <AdminLayout>
            {/* Custom ActionBar with integrated search and filter */}
            <div className="px-8 py-6 border-b border-zinc-700">
                <div className="flex justify-between items-center">
                    <h1 className="text-5xl">Orders</h1>
                    <div className="flex items-center space-x-3">
                        <div className="flex items-center bg-charleston-green border border-zinc-700 rounded-full">
                            {/* Search Input */}
                            <input
                                type="text"
                                placeholder="Search by mobile, external order ID, external reference num..."
                                value={mobileNumber}
                                onChange={(e) => handleSearch(e.target.value)}
                                className="bg-transparent px-5 py-2.5 text-white placeholder-zinc-500 w-[400px] focus:outline-none"
                            />
                            <svg 
                                className="w-5 h-5 text-zinc-500 mr-3" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        
                        {/* Status Filter */}
                        <div className="relative">
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="flex items-center gap-2 px-4 py-2.5 bg-charleston-green border border-zinc-700 rounded-full text-white hover:bg-zinc-800 transition-colors min-w-[220px]"
                            >
                                <span className="text-sm">
                                    <span className={selectedStatus ? getStatusColor(selectedStatus) : ""}>
                                        {ORDER_STATUSES.find(s => s.value === selectedStatus)?.label || "All Statuses"}
                                    </span>
                                </span>
                                <span className={`transition-transform inline-block ml-auto ${isDropdownOpen ? 'rotate-180' : ''}`}>▼</span>
                            </button>
                            
                            {isDropdownOpen && (
                                <div className="absolute top-full right-0 mt-1 w-56 bg-eerie-black border border-zinc-700 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                                    {ORDER_STATUSES.map(status => (
                                        <button
                                            key={status.value}
                                            onClick={() => handleStatusChange(status.value)}
                                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-charleston-green transition-colors ${
                                                selectedStatus === status.value ? 'bg-charleston-green' : ''
                                            } ${status.value ? getStatusColor(status.value) : 'text-white'}`}
                                        >
                                            {status.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            <Container>
                <div className="h-full flex flex-col justify-between">
                    {/* Orders List */}
                    <div className="flex-1">
                        {ordersLoading ? (
                            <div className="flex justify-center items-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                            </div>
                        ) : orders.length > 0 ? (
                            <div className="space-y-4">
                                {orders.map((order) => (
                                    <OrderCard 
                                        key={order.id || order.order_id || Math.random()} 
                                        order={order}
                                        onView={handleView}
                                    />
                                ))}
                            </div>
                        ) : searchDebounce && !ordersLoading ? (
                            <div className="bg-charleston-green rounded-lg p-12 text-center border border-zinc-700">
                                <p className="text-gray-400 text-lg">
                                    No orders found for "{searchDebounce}"
                                    {selectedStatus && ` with status "${ORDER_STATUSES.find(s => s.value === selectedStatus)?.label}"`}
                                </p>
                            </div>
                        ) : !searchDebounce && orders.length === 0 && !ordersLoading ? (
                            <div className="bg-charleston-green rounded-lg p-12 text-center border border-zinc-700">
                                <p className="text-gray-400 text-lg">No orders found</p>
                            </div>
                        ) : null}
                    </div>

                    {/* Pagination */}
                    {!ordersLoading && orders.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={ordersTotal}
                            itemsPerPage={itemsPerPage}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>
            </Container>
        </AdminLayout>
    );
};

export default Orders;